import type { ChangeEvent, ComponentProps, FC } from 'react';
import { useCallback } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { Range } from '@catalyze/ui';

import { Container, Row, StyledFormInput } from './styled';

import { ClearButton } from '../ClearButton';

type IDateRangeMenuProps = ComponentProps<typeof Container> & {
    inputName: string;
    lowerBoundText: string;
    upperBoundText: string;
    min?: string;
    max?: string;
};

export const DateRangeMenu: FC<IDateRangeMenuProps> = ({
    lowerBoundText,
    upperBoundText,
    min,
    max,
    inputName,
    ...props
}) => {
    const {
        field: { onChange, onBlur },
    } = useController({ name: inputName });
    const { resetField, watch } = useFormContext();
    const minId = `${inputName}.${Range.MIN}`;
    const maxId = `${inputName}.${Range.MAX}`;
    const [minValue, maxValue] = watch([minId, maxId], [min, max]);

    const onLowerChange = useCallback(
        (ev: ChangeEvent<HTMLInputElement>) => {
            const value = ev.target.value;
            onChange({
                [Range.MIN]: value.toString(),
            });
            onBlur();
        },
        [onChange, onBlur]
    );

    const onUpperChange = useCallback(
        (ev: ChangeEvent<HTMLInputElement>) => {
            const value = ev.target.value;
            onChange({
                [Range.MAX]: value.toString(),
            });
            onBlur();
        },
        [onChange, onBlur]
    );

    return (
        <Container {...props}>
            <Row style={{ marginBottom: 0 }}>
                <StyledFormInput
                    label={lowerBoundText}
                    id={minId}
                    type="date"
                    min={min}
                    max={maxValue || max}
                    onChange={onLowerChange}
                />
                <StyledFormInput
                    label={upperBoundText}
                    id={maxId}
                    type="date"
                    min={minValue || min}
                    max={max}
                    onChange={onUpperChange}
                />
            </Row>
            <ClearButton
                disabled={!(Boolean(minValue) || Boolean(maxValue))}
                onClick={() => resetField(inputName)}
            >
                Clear
            </ClearButton>
        </Container>
    );
};

DateRangeMenu.defaultProps = {
    min: undefined,
    max: undefined,
};
